import { clsx } from 'clsx'
import { motion, type HTMLMotionProps } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'

interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  children: ReactNode
}

export const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) => {
  const baseClasses = clsx(
    'inline-flex items-center justify-center font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-night-950',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'rounded-lg'
  )

  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg shadow-primary-500/20 focus:ring-primary-500 dark:bg-ocean-500 dark:hover:bg-ocean-400 dark:shadow-lg dark:shadow-ocean-500/20',
    secondary: 'bg-zinc-600 hover:bg-zinc-700 text-white shadow-md shadow-gray-500/15 focus:ring-night-600 dark:bg-night-750 dark:hover:bg-night-700 dark:shadow-none',
    ghost: 'hover:bg-gray-100 dark:hover:bg-night-850/60 text-gray-700 dark:text-slate-200 focus:ring-night-600',
    outline: 'border border-gray-300 dark:border-night-750/60 hover:bg-gray-50 dark:hover:bg-night-900/60 text-gray-700 dark:text-slate-100 shadow-sm shadow-gray-500/10 dark:shadow-none focus:ring-ocean-500'
  }

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  }

  return (
    <motion.button
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      )}
      {children}
    </motion.button>
  )
}
